import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type Subscription = {
  id: number;
  module_code: string;
  billing_cycle: string;
  status: string;
  activated_at: string | null;
  canceled_at: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  name: string;
  plugin_id: string | null;
  icon: string | null;
};

const statusLabels: Record<string, { label: string; color: string }> = {
  active: { label: 'Attivo', color: '#16a34a' },
  trialing: { label: 'In prova', color: '#2563eb' },
  past_due: { label: 'Pagamento in ritardo', color: '#dc2626' },
  canceled: { label: 'Disdetto', color: '#6b7280' },
  expired: { label: 'Scaduto', color: '#6b7280' },
  pending: { label: 'In attesa', color: '#f59e0b' },
};

export function TenantModulesPage() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    const res = await apiFetch('/api/v1/tenant/modules');
    const body = (await res.json().catch(() => ({}))) as {
      subscriptions?: Subscription[];
      message?: string;
      error?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `modules ${res.status}`);
    setSubs(Array.isArray(body.subscriptions) ? body.subscriptions : []);
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  async function onCancel(sub: Subscription) {
    if (!window.confirm(`Disdire «${sub.name || sub.module_code}»?`)) return;
    setBusy(sub.module_code);
    setError(null);
    setMessage(null);
    try {
      const res = await apiFetch('/api/v1/tenant/store/cancel', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ module_code: sub.module_code }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Modulo «${sub.name || sub.module_code}» disdetto.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="sk-saas-page">
      <header className="sk-saas-page__header">
        <div></div>
        <Link to="/tenant/store" className="sk-btn">
          Vai allo store
        </Link>
      </header>

      {error ? <p className="sk-error">{error}</p> : null}
      {message ? <p className="sk-ok">{message}</p> : null}

      <article className="sk-saas-card">
        <h2 className="sk-saas-card__title">Sottoscrizioni ({subs.length})</h2>
        <DataTable
          rows={subs}
          emptyMessage="Nessun modulo attivo. Vai allo store per acquistare."
          columns={[
            { key: 'name', label: 'Modulo', render: (v, row) => String(v || row.module_code) },
            { key: 'plugin_id', label: 'Plugin' },
            {
              key: 'status',
              label: 'Stato',
              render: (v) => {
                const s = statusLabels[String(v)] || { label: String(v), color: '#6b7280' };
                return <span style={{ fontWeight: 600, color: s.color }}>{s.label}</span>;
              },
            },
            { key: 'billing_cycle', label: 'Ciclo', render: (v) => (v === 'annual' ? 'Annuale' : 'Mensile') },
            {
              key: 'current_period_end',
              label: 'Rinnovo',
              render: (v) => (v ? new Date(String(v)).toLocaleDateString('it-IT') : '—'),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => {
                if (row.status === 'canceled' || row.status === 'expired') return null;
                return (
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet"
                    disabled={busy === row.module_code}
                    onClick={() => void onCancel(row)}
                  >
                    {busy === row.module_code ? '…' : 'Disdici'}
                  </button>
                );
              },
            },
          ]}
        />
      </article>
    </section>
  );
}
